import React, {
  useEffect,
  createContext,
  ReactNode,
  useContext,
  useReducer,
} from 'react';
import {
  Category,
  Service,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import {
  PartnerProfile,
  Pricing,
  SortingMethod,
} from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { GetPlaceResponse, Place } from '@wix/ambassador-atlas-v2-place/types';
import _ from 'lodash';
import { useRouter } from 'next/router';
import {
  useHttpClient,
  useExperiments,
  useErrorMonitor,
} from '@wix/fe-essentials-standalone';
import { Language } from '@common/types';
import {
  convertPricingToUSD,
  getCurrencyConvertedMaxPrice,
} from '@utils/currency';
import {
  MIN_PRICE,
  DEFAULT_CURRENCY,
  DEFAULT_PRICING,
  enableInternalMarketplaceSpec,
  ENABLE_YELL_REDIRECT,
} from '@utils/constants';
import { getServicesFiltersFromUrl, convertServicesToIds } from '@utils/route';
import {
  getServiceByPathName,
  getCategoryByPathName,
} from '@utils/servicesUtils';

interface ExploreContextType {
  state: State;
  dispatch: any;
}

interface ExploreContextProviderProps {
  children?: ReactNode;
  providers?: Array<PartnerProfile>;
  numberOfResults?: number;
  numberOfPages?: number;
  currentPage?: number;
  services?: Array<Category>;
  languages: Array<Language>;
  language: string;
  staticLocaleUrl: string;
  service?: Service;
  category?: Category;
  entry?: string;
  origin?: string;
  isMobileUserAgent: boolean;
  selectedPricing?: Pricing;
  selectedLanguages?: Array<Language>;
  selectedServices?: Array<Service>;
  selectedLocation?: Place;
  selectedSortingMethod?: SortingMethod;
  userCurrency?: string;
  currencyConversionRate?: number;
  appStatus?: string;
  reservedSlots?: number;
}

type State = {
  services: Array<Category>;
  selectedServices: Array<Service>;
  languages: Array<Language>;
  language: string;
  staticLocaleUrl: string;
  selectedLanguages: Array<Language>;
  selectedPricing: Pricing;
  currentPage: number;
  numberOfPages: number;
  numberOfResults: number;
  appStatus: string;
  openedFilterPopover: any | null;
  selectedSortingMethod: SortingMethod;
  selectedLocation: GetPlaceResponse['place'];
  providers: Array<PartnerProfile>;
  origin: string | undefined;
  entry: string | undefined;
  biArenaType: number;
  category: Category | any;
  service: Service | any;
  userCurrency: string;
  currencyConversionRate: number;
  maxPrice: number;
  shouldFocusOnPartnerCard: boolean;
  isMobileUserAgent: boolean;
  SSRFlow: boolean;
};

const ExploreContext = createContext<null | ExploreContextType>(null);

function dispatchMiddleware(dispatch: any, httpClient: any) {
  return async (action: any) => {
    switch (action.type) {
      case ACTIONS.FETCH_MORE_PROVIDERS:
        dispatch({
          type: ACTIONS.FETCHING_MORE_PROVIDERS,
        });

        const {
          selectedServices,
          selectedLanguages,
          selectedPricing,
          selectedSortingMethod,
          selectedLocation,
          currencyConversionRate,
          page,
          reservedSlots,
        } = action;

        const selectedPricingInUSD = convertPricingToUSD(
          selectedPricing,
          currencyConversionRate,
        );

        const {
          data: { partnerProfiles },
        } = await httpClient.post('/api/providers', {
          serviceIds: _.map(selectedServices, (service) => service.id),
          languages: _.map(selectedLanguages, (language) => language.id),
          pricing: selectedPricingInUSD,
          page,
          sortingMethod: selectedSortingMethod,
          selectedLocation,
          reservedSlots,
        });

        dispatch({
          type: ACTIONS.SET_MORE_PROVIDERS,
          providers: partnerProfiles,
          currentPage: page,
        });
        break;
      default:
        return dispatch(action);
    }
  };
}

function mainReducer(state: State, action: any) {
  switch (action.type) {
    case ACTIONS.SET_PROVIDERS: {
      const { providers } = action;
      return {
        ...state,
        providers,
      };
    }
    case ACTIONS.SET_MORE_PROVIDERS: {
      const { providers, currentPage, appStatus = 'initialized' } = action;
      return {
        ...state,
        appStatus,
        providers: [...state.providers, ...providers],
        currentPage,
      };
    }
    case ACTIONS.FETCHING_MORE_PROVIDERS: {
      return {
        ...state,
        SSRFlow: false,
        appStatus: 'fetching',
      };
    }
    case ACTIONS.LOADING: {
      return {
        ...state,
        SSRFlow: false,
        appStatus: 'loading',
      };
    }
    case ACTIONS.INITIALIZED: {
      return {
        ...state,
        appStatus: 'initialized',
      };
    }
    case ACTIONS.ERROR: {
      return {
        ...state,
        appStatus: 'error',
      };
    }
    case ACTIONS.SET_SERVICES: {
      const { services } = action;
      return {
        ...state,
        selectedServices: services,
      };
    }
    case ACTIONS.SET_SELECTED_LANGUAGES: {
      const { languages } = action;
      return {
        ...state,
        selectedLanguages: languages,
      };
    }
    case ACTIONS.SET_PRICING: {
      const { min, max } = action;
      return {
        ...state,
        selectedPricing: { min, max },
      };
    }
    case ACTIONS.SET_SELECTED_LOCATION: {
      const { location } = action;
      return {
        ...state,
        selectedLocation: location,
      };
    }
    case ACTIONS.SET_SORTING_METHOD: {
      const { sortingMethod } = action;
      return {
        ...state,
        selectedSortingMethod: sortingMethod,
      };
    }
    case ACTIONS.SET_PAGE: {
      const { currentPage } = action;
      return {
        ...state,
        currentPage,
      };
    }
    case ACTIONS.SET_NUMBER_OF_PAGES: {
      const { numberOfPages } = action;
      return {
        ...state,
        numberOfPages,
      };
    }
    case ACTIONS.SET_NUMBER_OF_RESULTS: {
      const { numberOfResults } = action;
      return {
        ...state,
        numberOfResults,
      };
    }
    case ACTIONS.FILTER_POPOVER_STATE: {
      const { panel, shouldOpen } = action;

      if (!shouldOpen) {
        return {
          ...state,
          openedFilterPopover: null,
        };
      }
      return {
        ...state,
        openedFilterPopover: panel,
      };
    }
    case ACTIONS.UPDATE_SEO_PARAMS: {
      const { category, service } = action;
      if (
        _.get(state.service, 'pathName') === service ||
        _.get(state.category, 'pathName') === category
      ) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        category,
        service,
      };
    }
    case ACTIONS.SHOULD_FOCUS_ON_PARTNER_CARD: {
      const { shouldFocusOnPartnerCard } = action;
      return {
        ...state,
        shouldFocusOnPartnerCard,
      };
    }
    default:
      throw new Error();
  }
}

export const ACTIONS = {
  FETCH_MORE_PROVIDERS: 'FETCH_MORE_PROVIDERS',
  SET_SERVICES: 'SET_SERVICES',
  SET_SELECTED_LANGUAGES: 'SET_SELECTED_LANGUAGES',
  SET_MORE_PROVIDERS: 'SET_MORE_PROVIDERS',
  SET_PROVIDERS: 'SET_PROVIDERS',
  SET_PRICING: 'SET_PRICING',
  SET_SORTING_METHOD: 'SET_SORTING_METHOD',
  SET_SELECTED_LOCATION: 'SET_SELECTED_LOCATION',
  SET_PAGE: 'SET_PAGE',
  SET_NUMBER_OF_PAGES: 'SET_NUMBER_OF_PAGES',
  SET_NUMBER_OF_RESULTS: 'SET_NUMBER_OF_RESULTS',
  LOADING: 'LOADING',
  INITIALIZED: 'INITIALIZED',
  ERROR: 'ERROR',
  UPDATE_URL_WITH_LANGUAGES: 'UPDATE_URL_WITH_LANGUAGES',
  FILTER_POPOVER_STATE: 'FILTER_POPOVER_STATE',
  FETCHING_MORE_PROVIDERS: 'FETCHING_MORE_PROVIDERS',
  UPDATE_SEO_PARAMS: 'UPDATE_SEO_PARAMS',
  SHOULD_FOCUS_ON_PARTNER_CARD: 'SHOULD_FOCUS_ON_PARTNER_CARD',
};

export function ExploreContextProvider(props: ExploreContextProviderProps) {
  const { experiments } = useExperiments({ readOnly: true });
  const errorMonitor = useErrorMonitor();
  const isInternal = experiments.enabled(enableInternalMarketplaceSpec);
  const reservedSlots = props.reservedSlots ?? 0;

  const biArenaType = isInternal ? 2 : 1;

  const [state, dispatch] = useReducer(mainReducer, {
    appStatus: props.appStatus || 'initialized',
    SSRFlow: true,
    providers: props.providers || [],
    services: props.services || [],
    languages: props.languages,
    language: props.language || 'en',
    staticLocaleUrl: props.staticLocaleUrl,
    selectedServices: props.selectedServices || [],
    selectedLanguages: props.selectedLanguages || [],
    selectedPricing: props.selectedPricing || DEFAULT_PRICING,
    currentPage: props.currentPage || 1,
    numberOfPages: props.numberOfPages || 1,
    numberOfResults: props.numberOfResults || 0,
    openedFilterPopover: null,
    selectedSortingMethod:
      props.selectedSortingMethod || SortingMethod.RELEVANCE,
    selectedLocation: props.selectedLocation || null,
    origin: props.origin || undefined,
    entry: props.entry || '',
    biArenaType,
    category: _.get(props, 'category', ''),
    service: _.get(props, 'service', ''),
    userCurrency: props.userCurrency ?? DEFAULT_CURRENCY,
    currencyConversionRate: props.currencyConversionRate ?? 1,
    maxPrice: getCurrencyConvertedMaxPrice(props.currencyConversionRate ?? 1),
    shouldFocusOnPartnerCard: false,
    isMobileUserAgent: props?.isMobileUserAgent,
  });

  const router = useRouter();
  const httpClient = useHttpClient();

  function updateSeoParamsFromUrlPath() {
    const { all, serviceIds } = router.query;
    let category = null;
    let service = null;
    if (!serviceIds) {
      const pair = all && all.length > 0 ? all : [];
      const categoryName = pair[0];
      const serviceName = pair[1];
      service = getServiceByPathName(state.services, serviceName);
      category = getCategoryByPathName(state.services, categoryName);
    }

    dispatch({
      type: ACTIONS.UPDATE_SEO_PARAMS,
      category,
      service,
    });
    return { category, service };
  }

  function updateLanguagesFiltersFromUrl() {
    const languagesFromUrl = router.query.lang as string;
    const selectedLanguages = _.filter(state.languages, (item) =>
      languagesFromUrl?.split(',').includes(item.id),
    );
    dispatch({
      type: ACTIONS.SET_SELECTED_LANGUAGES,
      languages: selectedLanguages,
    });
    return _.map(selectedLanguages, (language) => language.id);
  }

  function updateCurrentPageFromUrl() {
    const currentPage = _.toNumber(router.query.page) || 1;
    dispatch({ type: ACTIONS.SET_PAGE, currentPage });
    return currentPage;
  }

  function updateServicesFiltersFromUrl() {
    const { category, service } = updateSeoParamsFromUrlPath();
    const services = getServicesFiltersFromUrl(
      router.query,
      state.services,
      service,
      category,
    );
    dispatch({ type: ACTIONS.SET_SERVICES, services });
    return services;
  }

  function updatePricingFilterFromUrl(): Pricing {
    const min = _.toNumber(router.query.min || MIN_PRICE);
    const max = _.toNumber(router.query.max || undefined);
    dispatch({ type: ACTIONS.SET_PRICING, min, max });
    return { min, max };
  }

  async function updateLocationFilterFromUrl() {
    const savedPlaceId = router.query.location;
    if (savedPlaceId) {
      try {
        const {
          data: { place },
        } = await httpClient.post<{ place?: Place }>('/api/location/getPlace', {
          searchId: savedPlaceId,
        });
        dispatch({ type: ACTIONS.SET_SELECTED_LOCATION, location: place });
        return place;
      } catch (err) {
        errorMonitor.captureException(err as Error);
      }
    } else {
      dispatch({ type: ACTIONS.SET_SELECTED_LOCATION, location: null });
    }
  }

  function updateSortingMethodFromUrl() {
    const sortingMethodFromUrl = router.query.sort;
    const _sortingMethod =
      SortingMethod[sortingMethodFromUrl as keyof typeof SortingMethod] ??
      SortingMethod.RELEVANCE;
    dispatch({
      type: ACTIONS.SET_SORTING_METHOD,
      sortingMethod: sortingMethodFromUrl,
    });
    return _sortingMethod;
  }

  async function fetchProviders(
    serviceIds: Array<string>,
    page: number,
    pricing: Pricing,
    languages: Array<string>,
    selectedLocation?: Place,
    sortingMethod?: SortingMethod,
  ) {
    try {
      dispatch({
        type: ACTIONS.LOADING,
      });

      const selectedPricingInUSD = convertPricingToUSD(
        pricing,
        state.currencyConversionRate,
      );

      const isBriefPromoCardEnabled =
        !experiments.enabled(ENABLE_YELL_REDIRECT);

      const {
        data: { partnerProfiles, numberOfPages, numberOfResults },
      } = await httpClient.post<any>('/api/providers', {
        serviceIds,
        languages,
        pricing: selectedPricingInUSD,
        page,
        sortingMethod,
        selectedLocation,
        reservedSlots,
      });

      dispatch({
        type: ACTIONS.SET_PROVIDERS,
        providers: partnerProfiles,
      });
      dispatch({
        type: ACTIONS.SET_NUMBER_OF_PAGES,
        numberOfPages,
      });
      dispatch({
        type: ACTIONS.SET_NUMBER_OF_RESULTS,
        numberOfResults,
      });
      dispatch({
        type: ACTIONS.INITIALIZED,
      });
    } catch (err) {
      errorMonitor.captureException(err as Error);
    }
  }

  useEffect(() => {
    const handleRouterChange = async () => {
      const languages = updateLanguagesFiltersFromUrl();
      const serviceIds = convertServicesToIds(updateServicesFiltersFromUrl());
      const currentPage = updateCurrentPageFromUrl();
      const selectedPricing = updatePricingFilterFromUrl();
      const selectedLocation = await updateLocationFilterFromUrl();
      const sortingMethod = updateSortingMethodFromUrl();

      fetchProviders(
        serviceIds,
        currentPage,
        selectedPricing,
        languages,
        selectedLocation,
        sortingMethod,
      );
    };

    router.events.on('routeChangeComplete', handleRouterChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouterChange);
    };
  }, [router]);

  return (
    <ExploreContext.Provider
      value={{ state, dispatch: dispatchMiddleware(dispatch, httpClient) }}
    >
      {props?.children}
    </ExploreContext.Provider>
  );
}

export function useExploreContext() {
  const exploreContext = useContext(ExploreContext);
  if (!exploreContext) {
    throw new Error(
      'useExploreContext must be used within the ExploreContext.Provider',
    );
  }
  return exploreContext;
}
